import * as networkManager from "@utils/managers/networking/NetworkManager";
import { GroupOrganiseModel, NLMailboxProfile, NLMailboxProfileType, NLProfile, NLUserGroup, NLUserMailState, NLUserMailStateNames, NLUserSenderGroup, OnboardingProfile } from "../../models/Models";
import * as analyticsManager from "@utils/managers/AnalyticsManager";
import { kAnalyticsConstants } from "@utils/constants/AnalyticsConstants";
import { ChangeUserMailStatesService } from "@utils/managers/backendMailManager/mailboxFunctions/ChangeUserMailStatesService";
import * as mailManager from "@utils/managers/MailManager";
import { FiltersService } from "@utils/managers/backendMailManager/mailboxFunctions/FiltersService";

export class MailboxIntegrationManager {
	onboardingProfile: OnboardingProfile;
	user: NLProfile;
	reference: MailboxIntegrationManager.Reference;

	constructor(onboardingProfile: OnboardingProfile, user: NLProfile, reference: MailboxIntegrationManager.Reference) {
		this.onboardingProfile = onboardingProfile;
		this.user = user;
		this.reference = reference;
	}

	applyNewsletterChanges = (isCreateGroups: boolean = true): Promise<any[]> => {
		return new Promise((resolve, reject) => {
			var promises: Promise<any>[] = [];
			const feedArrayPromise = this.postFeedArray(this.onboardingProfile);
			const inboxArrayPromise = this.postInboxArray(this.onboardingProfile);
			promises = [feedArrayPromise, inboxArrayPromise];

			var resultsArray: any[] = [];

			Promise.all(promises)
				.then((results) => {
					resultsArray.push([...results]);
					return this.syncFilters(this.onboardingProfile);
				})
				.then(() => {
					return this.createUserGroups(this.onboardingProfile, isCreateGroups);
				})
				.finally(() => {
					this.markProfileAsSetup(this.onboardingProfile, this.user, this.reference).then((profile) => {
						resultsArray.push(profile);
						resolve(resultsArray);
					});
				})
				.catch((error) => {
					reject(error);
				});
		});
	};

	postOneClickSubscribes = (onboardingProfile: OnboardingProfile): Promise<NLUserMailState[] | undefined> => {
		return new Promise((resolve, reject) => {
			const oneClickSubscribeArray: number[] = onboardingProfile.persistence.oneClickSubscribeArray.map((x) => x.id);

			if (oneClickSubscribeArray.length == 0) {
				resolve(undefined);
				return;
			}

			networkManager
				.postOneClickSubscribeNewsletters(oneClickSubscribeArray, kAnalyticsConstants.Reference.onboarding, false)
				.then((userMailStates) => {
					if (userMailStates.length > 0) {
						for (let userMailState of userMailStates) {
							analyticsManager.recordEvent(kAnalyticsConstants.App.oneClickSubscribeConversion, { sender_name: userMailState.sender_name ?? "", sender_address: userMailState.sender_address, reference: kAnalyticsConstants.Reference.onboarding });
						}
					}

					resolve(userMailStates);
				})
				.catch((error) => {
					reject(error);
				});
		});
	};

	postFeedArray = (onboardingProfile: OnboardingProfile): Promise<NLUserMailState[] | undefined> => {
		return new Promise((resolve, reject) => {
			const feedArray: number[] = onboardingProfile.persistence.feedArray;

			if (feedArray.length == 0) {
				resolve(undefined);
				return;
			}

			const userMailStateRequests = new ChangeUserMailStatesService.QueryBuilder(onboardingProfile.persistence.mailboxProfile!, NLUserMailStateNames.feed, onboardingProfile.persistence.feedArray);

			mailManager.changeState([userMailStateRequests], false, (userMailStates, error) => {
				if (error) {
					reject(error);
					return;
				}
				resolve(userMailStates);
			});
		});
	};

	postInboxArray = (onboardingProfile: OnboardingProfile): Promise<NLUserMailState[] | undefined> => {
		return new Promise((resolve, reject) => {


			if (onboardingProfile.persistence.prospectArray.length === 0) {
				resolve(undefined);
				return;
			}

			let prospectsArray = onboardingProfile.persistence.prospectArray;
			let feedArray = onboardingProfile.persistence.feedArray;
			let inboxArray = prospectsArray.filter((x) => !feedArray.includes(x.sender_id));
			let inboxSendersArray = inboxArray.map((x) => x.sender_id);

			if (inboxSendersArray.length === 0) {
				resolve(undefined);
				return;
			}

			const inboxRequestModel = new ChangeUserMailStatesService.QueryBuilder(onboardingProfile.persistence.mailboxProfile!, NLUserMailStateNames.inbox, inboxSendersArray);

			mailManager.changeState([inboxRequestModel], false, (userMailStates, error) => {
				if (error) {
					reject(error);
					return;
				}
				resolve(userMailStates);
			});

		});
	};

	syncFilters = (onboardingProfile: OnboardingProfile): Promise<void> => {
		return new Promise((resolve, reject) => {
			const mailboxProfile = onboardingProfile.persistence.mailboxProfile;
			if (mailboxProfile!.type == NLMailboxProfileType.Internal) {
				resolve();
				return;
			}

			const filterRequestModels = [new FiltersService.QueryBuilder(onboardingProfile.persistence.mailboxProfile!)];
			const filterService = new FiltersService(filterRequestModels);

			filterService.syncFilters((syncFiltersResponse, error) => {
				if (!syncFiltersResponse || error) {
					reject(error);
					return;
				}

				const syncResponse = syncFiltersResponse.filter((x) => x.mailboxProfileId === mailboxProfile!.id);

				if (syncResponse[0].error) {
					reject(syncResponse[0].error);
					return;
				}

				resolve();
			});
		});
	};

	createUserGroups = (onboardingProfile: OnboardingProfile, isCreateGroups: boolean): Promise<void> => {
		return new Promise(async (resolve, reject) => {
			if (!isCreateGroups) {
				resolve();
				return;
			}

			const prospectArray = onboardingProfile.persistence.prospectArray;
            var groupKeyValue: GroupOrganiseModel[] = [];

			for (let prospect of prospectArray) {

				if (!onboardingProfile.persistence.feedArray.includes(prospect.sender_id)) {
					continue;
				}

				for (let group of prospect.sender_groups) {
					let alreadyAddedGroup = groupKeyValue.find((x) => x.groupName === group);
					if (alreadyAddedGroup) {
						let idx = groupKeyValue.indexOf(alreadyAddedGroup);
						alreadyAddedGroup.senders.push(prospect.sender_address);
						groupKeyValue[idx] = alreadyAddedGroup;
					} else {
						groupKeyValue.push({ groupName: group, senders: [prospect.sender_address] });
					}
				}
	
			}

			const generateNewGroup = (groupName: string, idx: number): NLUserGroup => {
				let newGroup = new NLUserGroup();
				newGroup.group_name = groupName;
				newGroup.group_index = idx;
				return newGroup;
			}

			var groupArray: NLUserGroup[] = [];
			for (let i = 0; i < groupKeyValue.length; i++) {
				groupArray.push(generateNewGroup(groupKeyValue[i].groupName, i + 2));
			}

			if (groupArray.length === 0) {
				resolve();
				return;
			}


			let userGroups = await networkManager.postUserGroups(groupArray);
			
			for (let userGroup of userGroups) {

				const groupObject = groupKeyValue.find((x) => x.groupName === userGroup.group_name);

				var senders: NLUserSenderGroup[] = [];		
				for (let sender of groupObject!.senders) {
					let newSender = new NLUserSenderGroup();
					newSender.sender_address = sender;
					newSender.user_group = userGroup.id;
					newSender.sender_address_index = 0;
					senders.push(newSender);
				}

				await networkManager.createUserSenderGroups(senders);
			}

			resolve();
			
		});
	}

	markProfileAsSetup = (onboardingProfile: OnboardingProfile, user: NLProfile, reference: MailboxIntegrationManager.Reference): Promise<NLProfile | undefined> => {
		return new Promise((resolve, _reject) => {
			switch (reference) {
				case MailboxIntegrationManager.Reference.signUp:
					networkManager
						.updateUserProfile({ is_profile_setup: true })
						.then((updatedProfile) => {
							var modifiedMailboxProfile = new NLMailboxProfile(onboardingProfile.persistence.mailboxProfile!);
							modifiedMailboxProfile.is_profile_setup = true;
							networkManager
								.updateMailboxProfile(modifiedMailboxProfile)
								.then((updatedProfile) => {
									resolve(updatedProfile);
								})
								.catch((_error) => {
									resolve(undefined);
								});
						})
						.catch((_error) => {
							resolve(undefined);
						});
					break;
				case MailboxIntegrationManager.Reference.nonSignUp:
					const mailboxProfile = onboardingProfile.persistence.mailboxProfile;
					if (mailboxProfile!.type == NLMailboxProfileType.Internal) {
						resolve(user);
						return;
					}

					var modifiedMailboxProfile = new NLMailboxProfile(mailboxProfile!);
					modifiedMailboxProfile.is_profile_setup = true;
					networkManager
						.updateMailboxProfile(modifiedMailboxProfile)
						.then((updatedProfile) => {
							resolve(updatedProfile);
						})
						.catch((_error) => {
							resolve(undefined);
						});
					break;
			}
		});
	};
}

export namespace MailboxIntegrationManager {
	export enum Reference {
		signUp,
		nonSignUp,
	}
}
